import React, { useEffect } from 'react';
import { create, useTheme, percent } from '@amcharts/amcharts4/core';
import { XYChart, CategoryAxis, ValueAxis, ColumnSeries, Legend, LabelBullet } from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

const EmploymentStatusChart = ({ chartData }) => {
  useTheme(am4themes_animated);

  useEffect(() => {
    const chartInstances = [];

    chartData.forEach((yearData) => {
      const chartId = `employment-status-${yearData.year}`;
      let chart = create(chartId, XYChart);
      chart.paddingRight = 20;
      chart.logo.disabled = true;

      // Transform data for the chart
      chart.data = yearData.status_data.map((statusData) => ({
        ...statusData.bank_percentages,
        label: statusData.label,
      }));

      // Calculate the maximum value for the ValueAxis
      const maxPercentage = Math.max(
        ...chart.data.map((item) =>
          Object.values(item).reduce((sum, value) => (typeof value === "number" ? sum + value : sum), 0)
        )
      );

      // Chart title
      let title = chart.titles.create();
      title.text = `${yearData.year}`;
      title.fontSize = 20;
      title.fontFamily = "Arial, sans-serif";
      title.fill = "#333";

      // Category Axis (Y-axis)
      let categoryAxis = chart.yAxes.push(new CategoryAxis());
      categoryAxis.dataFields.category = "label";
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 20;
      categoryAxis.renderer.grid.template.disabled = true;

      // Value Axis (X-axis)
      let valueAxis = chart.xAxes.push(new ValueAxis());
      valueAxis.title.text = "";
      valueAxis.min = 0;
      valueAxis.max = maxPercentage > 100 ? maxPercentage : 100; // Adjust max value
      valueAxis.renderer.grid.template.disabled = true;
      valueAxis.renderer.labels.template.disabled = true;

      const bankColors = {
        "Absa": "#DF1641",
        "Capitec": "#FF3393",
        "FNB": "#00CAFF",
        "Nedbank": "#006E2C",
        "Standard Bank": "#0033A1",
        "TymeBank": "#FFC000",
      };

      // Add series for each bank
      Object.keys(bankColors).forEach((bank) => {
        let series = chart.series.push(new ColumnSeries());
        series.dataFields.valueX = bank;
        series.dataFields.categoryY = "label";
        series.name = bank;
        series.stacked = true;
        series.fill = bankColors[bank];
        series.stroke = bankColors[bank];
        series.columns.template.tooltipText = `{name}: {valueX}%`;
        series.columns.template.height = percent(60);

        // Add labels to the bars
        let labelBullet = series.bullets.push(new LabelBullet());
        labelBullet.label.text = "{valueX}"; // Remove percentage symbol
        labelBullet.label.fill = "#fff"; // White text
        labelBullet.locationX = 0.5; // Center the label on the bar
        labelBullet.label.hideOversized = true; // Hide if it doesn't fit
        labelBullet.label.fontSize = 12;
        labelBullet.label.truncate = false; // Prevent truncation
      });

      // Reduce the width of the stacked bars
      chart.columnWidth = 0.6; // Adjust this value as needed (0.6 means 60% width)

      // Add legend
      chart.legend = new Legend();
      chartInstances.push(chart);
    });

    return () => {
      // Dispose of all chart instances when the component unmounts
      chartInstances.forEach((chart) => chart.dispose());
    };
  }, [chartData]);

  return (
    <div>
      {chartData.map((yearData) => (
        <div
          key={yearData.year}
          id={`employment-status-${yearData.year}`}
          style={{ width: "100%", height: "500px", marginBottom: "20px" }}
        ></div>
      ))}
    </div>
  );
};

export default EmploymentStatusChart;
